/**
 * Browser-side twin of the Pages Worker upload whitelist (`sniffAndValidateUpload`
 * in _worker.js). The worker only stores JPEG, PNG, PDF, GIF and WebP and answers
 * anything else with 415, while the portal's `accept="image/*"` pickers happily
 * offer HEIC, AVIF, BMP or TIFF. Convert those to JPEG before upload, and reject
 * undecodable files before any bytes are sent.
 */
import { canvasToBlob } from "@/lib/imageUtils";

export type UploadFormat =
  | "jpeg" | "png" | "pdf" | "gif" | "webp"
  | "heic" | "avif" | "bmp" | "tiff" | "unknown";

/** Safe-to-report description of a picked file. Never includes the filename. */
export interface UploadFileInfo {
  format: UploadFormat;
  extension?: string;
  mime?: string;
  converted?: boolean;
}

export class UploadFormatError extends Error {
  constructor(public readonly info: UploadFileInfo) {
    super(`Unsupported upload format (${info.format})`);
    this.name = "UploadFormatError";
  }
}

const SERVER_ACCEPTED = new Set<UploadFormat>(["jpeg", "png", "pdf", "gif", "webp"]);
// Keeps converted images well under the 10 MB upload limit and inside the
// ~16.7 MP canvas limit of iOS Safari (48 MP iPhone photos exceed it).
const MAX_CONVERTED_SIDE = 4096;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const HEIF_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs", "mif1", "msf1"]);

const ascii = (bytes: Uint8Array, start: number, end: number) =>
  String.fromCharCode(...bytes.subarray(start, end));

export function sniffUploadFormat(bytes: Uint8Array): UploadFormat {
  const startsWith = (...sig: number[]) => bytes.length >= sig.length && sig.every((b, i) => bytes[i] === b);
  if (startsWith(0xff, 0xd8, 0xff)) return "jpeg";
  if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "png";
  if (ascii(bytes, 0, 4) === "%PDF") return "pdf";
  if (["GIF87a", "GIF89a"].includes(ascii(bytes, 0, 6))) return "gif";
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") return "webp";
  if (ascii(bytes, 4, 8) === "ftyp") {
    // ISO-BMFF: box size, "ftyp", major brand, minor version, compatible brands.
    // AVIF also lists "mif1", so check it before the generic HEIF brands.
    const boxEnd = Math.min(bytes.length, ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0);
    const brands = [ascii(bytes, 8, 12)];
    for (let i = 16; i + 4 <= boxEnd; i += 4) brands.push(ascii(bytes, i, i + 4));
    if (brands.some((b) => b === "avif" || b === "avis")) return "avif";
    if (brands.some((b) => HEIF_BRANDS.has(b))) return "heic";
  }
  if (startsWith(0x42, 0x4d)) return "bmp";
  if (startsWith(0x49, 0x49, 0x2a, 0x00) || startsWith(0x4d, 0x4d, 0x00, 0x2a)) return "tiff";
  return "unknown";
}

function describeFile(file: File, format: UploadFormat): UploadFileInfo {
  const extension = /\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1].toLowerCase();
  const mime = /^[a-z]+\/[a-z0-9.+-]{1,40}$/i.test(file.type) ? file.type.toLowerCase() : undefined;
  return { format, extension, mime };
}

async function decodeToJpeg(source: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  try {
    const scale = Math.min(1, MAX_CONVERTED_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    // JPEG has no alpha: flatten transparent areas to white, not black.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await canvasToBlob(canvas, "image/jpeg", 0.9);
  } finally {
    bitmap.close();
  }
}

// Fallback for browsers without native HEIC decoding (everything except Safari).
async function heicToJpeg(source: Blob): Promise<Blob> {
  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({ blob: source, toType: "image/jpeg", quality: 0.9 });
  const jpeg = Array.isArray(converted) ? converted[0] : converted;
  return jpeg.size > MAX_UPLOAD_BYTES ? decodeToJpeg(jpeg) : jpeg;
}

/**
 * Returns a file the upload worker will accept: the original when its bytes
 * already match the whitelist, otherwise a JPEG conversion. Throws
 * UploadFormatError when the browser cannot decode the file.
 */
export async function prepareUploadFile(file: File): Promise<{ file: File; info: UploadFileInfo }> {
  // Empty files are rejected by the worker with a clearer 400.
  if (file.size === 0) return { file, info: describeFile(file, "unknown") };
  const format = sniffUploadFormat(new Uint8Array(await file.slice(0, 64).arrayBuffer()));
  const info = describeFile(file, format);
  if (SERVER_ACCEPTED.has(format)) return { file, info };

  // Native decoding first: Safari reads HEIC itself, and handles files heic2any cannot.
  const jpeg = await decodeToJpeg(file).catch(() => null)
    ?? (format === "heic" ? await heicToJpeg(file).catch(() => null) : null);
  if (!jpeg) throw new UploadFormatError(info);
  const name = `${file.name.replace(/\.[^.]*$/, "") || "upload"}.jpg`;
  return { file: new File([jpeg], name, { type: "image/jpeg" }), info: { ...info, converted: true } };
}
